export type City = {
  id: string;
  country_code: string;
  name: string;
  region_name: string | null;
  slug: string;
  timezone: string;
  center: { latitude: string | null; longitude: string | null };
};

export type CityPolicy = {
  mode: 'pilot_only' | 'all_discovered';
  pilot_city: City | null;
  revision: number;
};

export type CityContextStatus = 'available' | 'pilot_restricted' | 'not_discovered' | 'city_unavailable' | 'location_unresolved' | 'city_not_found';
export type DetectedLocation = { name: string; region_name: string | null; country_code: string };
export type CityContextResponse = { status: CityContextStatus; city: City | null; detected_location: DetectedLocation | null; policy: CityPolicy };
export type CitySelectionSource = 'manual' | 'geolocation' | 'restored' | 'explicit_link';
export type StoredCitySelection = { city: string; manual: boolean; snapshot: City | null };

export const CITY_CONTEXT_REFRESH_EVENT = 'hopper:city-context-refresh';

const STORAGE_KEY = 'hopper.city.selection.v2';
const LEGACY_MANUAL_KEY = 'hopper.city.manual.v1';

let csrfToken: string | null = null;
let lastResolvedStatus: CityContextStatus | null = null;

async function csrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch('/api/v1/auth/csrf', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  const body = await response.json() as { csrf_token?: string };
  if (!response.ok || !body.csrf_token) throw new Error('csrf_failed');
  csrfToken = body.csrf_token;
  return csrfToken;
}

function parsedCitySnapshot(value: unknown): City | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<City>;
  if (
    typeof candidate.id !== 'string'
    || !candidate.id
    || typeof candidate.name !== 'string'
    || typeof candidate.slug !== 'string'
    || typeof candidate.country_code !== 'string'
    || typeof candidate.timezone !== 'string'
    || !candidate.center
    || typeof candidate.center !== 'object'
  ) return null;

  const center = candidate.center as Partial<City['center']>;
  const latitudeValid = center.latitude === null || typeof center.latitude === 'string';
  const longitudeValid = center.longitude === null || typeof center.longitude === 'string';
  if (!latitudeValid || !longitudeValid) return null;

  return candidate as City;
}

export async function resolveCityContext(input: { city: string } | { latitude: number; longitude: number }, signal?: AbortSignal): Promise<CityContextResponse> {
  lastResolvedStatus = null;
  const token = await csrf();
  const response = await fetch('/api/v1/city-context/resolve', {
    method: 'POST', credentials: 'same-origin', signal,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
    body: JSON.stringify(input),
  });
  if (response.status === 503) throw new Error('provider_unavailable');
  if (!response.ok) throw new Error(`city_context_${response.status}`);

  const result = await response.json() as CityContextResponse;
  lastResolvedStatus = result.status;

  if ('city' in input && result.status === 'available' && result.city) {
    const stored = restoredCitySelection();
    const samePersistedCity = stored?.city === input.city;
    if (samePersistedCity) persistCitySelection(result.city, stored.manual);

    // A same-city re-resolution while Map is already mounted (manual selection or
    // a policy revision after returning from admin) does not change activeCity.id.
    // Refresh only that mounted map. A new city changes activeCity.id itself, and a
    // cold restore has no map yet, so neither path needs a second map request.
    if (samePersistedCity && document.querySelector('.map-stage')) {
      window.setTimeout(() => window.dispatchEvent(new Event(CITY_CONTEXT_REFRESH_EVENT)), 0);
    }
  }

  return result;
}

export async function loadCities(query = '', signal?: AbortSignal): Promise<City[]> {
  const params = new URLSearchParams(); if (query.trim()) params.set('q', query.trim());
  const response = await fetch(`/api/v1/cities${params.size ? `?${params}` : ''}`, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`cities_${response.status}`);
  return ((await response.json()) as { cities?: City[] }).cities ?? [];
}

export function restoredCitySelection(): StoredCitySelection | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { city?: unknown; manual?: unknown; snapshot?: unknown };
      if (typeof parsed.city === 'string' && parsed.city && typeof parsed.manual === 'boolean') {
        const snapshot = parsedCitySnapshot(parsed.snapshot);
        return { city: parsed.city, manual: parsed.manual, snapshot: snapshot?.id === parsed.city ? snapshot : null };
      }
    }
    const legacy = localStorage.getItem(LEGACY_MANUAL_KEY);
    return legacy ? { city: legacy, manual: true, snapshot: null } : null;
  } catch { return null; }
}

export function persistCitySelection(city: City, manual: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ city: city.id, manual, snapshot: city } satisfies StoredCitySelection));
    localStorage.removeItem(LEGACY_MANUAL_KEY);
  } catch { /* storage unavailable */ }
}

export function clearCitySelection(): void {
  // A restored city is a useful last-known browsing preference. Do not erase it
  // on a transient network/provider failure, pilot restriction, or temporary
  // readiness state: doing so makes the next page load fall through to browser
  // geolocation and wastes reverse-geocoder calls. Only a definitive missing
  // city invalidates the persisted reference.
  if (lastResolvedStatus !== 'city_not_found') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_MANUAL_KEY);
  } catch { /* storage unavailable */ }
  lastResolvedStatus = null;
}
